/* eslint-disable react/no-unused-state */
import React from 'react';
import PropTypes from 'prop-types';
import { bind, noop } from 'underscore';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FilterViewManagerWithGlobalFilterContext } from '@bingads-webui-campaign-react/filter-view-manager';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { TopGridPanel } from '@bingads-webui-campaign-react/top-grid-panel';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { getToolbarConfig } from './toolbar-config';
import { createOverviewGrid } from './overview-page-factory';
import { getOverviewPageStyles } from '../../style';
import { SubaccountSelectorPanel } from './components/subaccount-selector-panel';
import { overviewGridPickerConfig } from './overview-grid-picker-config';

const StyledGridViewWrapperWithFilterContext = withGlobalFilter(StyledGridViewWrapper);
class HotelCenterOverviewNew extends React.PureComponent {
  constructor(props) {
    super(props);
    this.viewport = React.createRef();
    this.state = {
      backboneGridView: null,
      isGridValid: false,
      hotelCenterI18n: null,
    };
  }

  componentDidMount() {
    this.createGrid();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.scope !== this.props.scope) {
      this.createGrid();
    }
    if (prevProps.gridName !== this.props.gridName) {
      this.createGrid();
    }
  }

  onSubaccountChangedFromSelector(selectSubAccount) {
    if (selectSubAccount.Id !== this.props.selectedSubaccountId) {
      this.state.backboneGridView.onSubaccountChangedForUINext(selectSubAccount.Id);
      this.props.updateSelectedSubaccount(selectSubAccount.Id);
    }
  }

  onSubaccountChangedFromInjectedView(selectedSubaccountId) {
    this.props.updateSelectedSubaccount(selectedSubaccountId);
  }

  setGridState = (isValid) => {
    this.setState({ isGridValid: isValid });
  };

  refreshGridForUINext = (gridName) => {
    const { history } = this.props;
    if (history) {
      history.push({
        pathname: `/campaign/vnext/hotelcenter/overview/${gridName.toLowerCase()}`,
        search: history.location.search,
      });
    }
  };

  jumpToReconReports = () => {
    const { history } = this.props;
    if (history) {
      history.push({
        pathname: '/campaign/vnext/hotelcenter/uploadedreconreports',
        search: history.location.search,
      });
    }
  };

  createGrid() {
    const {
      history,
      i18n,
      serverContext,
      scope,
      siteMap,
      permissions,
      preferenceService,
      resetToolbarVisibility,
      toolbarPlugin,
      crossPageSelectPlugin,
      toolbarSelectedConfig,
      toolbarUnselectedConfig,
      onGridInjected,
      updateToolbar,
      perfMarker,
      gridName,
    } = this.props;
    this.setGridState(false);
    const createGridPromise = createOverviewGrid({
      serverContext,
      scope,
      siteMap,
      permissions,
      preferenceService,
      toolbarPlugin,
      crossPageSelectPlugin,
      perfMarker,
      history,
      i18n,
      gridName,
      viewport: this.viewport.current,
      updateSelectedSubaccount: (subaccountId) => {
        this.onSubaccountChangedFromInjectedView(subaccountId);
      },
      refreshGridForUINext: this.refreshGridForUINext,
      jumpToReconReports: this.jumpToReconReports,
      gridRefreshed: () => this.setGridState(true),
    });

    const OverviewPageView = React.lazy(() =>
      createGridPromise.then(({ injectedView, hotelCenterI18n }) => {
        onGridInjected({
          backboneGridView: injectedView,
          deselectAll: injectedView.grid.gridView.deselectAll.bind(injectedView.grid.gridView),
        });

        this.setState({
          backboneGridView: injectedView,
          hotelCenterI18n,
        });

        return {
          default: () => (<StyledGridViewWrapperWithFilterContext gridView={injectedView} />),
        };
      }).catch(err => err));
    this.overviewGrid = (<OverviewPageView />);
    const toolbarEditor = getToolbarEditor({
      toolbarConfig: { toolbarSelectedConfig, toolbarUnselectedConfig },
      updateToolbar,
    });
    const gridPickerConfig = overviewGridPickerConfig({ i18n, history, gridName, permissions });
    toolbarEditor.update(gridPickerConfig);
    const FilterBarView = React.lazy(() => createGridPromise.then(({ filterService, filterConfig }) => {
      const { i18n: legacyI18n, ...filterProps } = filterConfig;
      const updateToolbarBinded = bind(toolbarEditor.update, toolbarEditor);

      return {
        default: () => (
          <FilterViewManagerWithGlobalFilterContext
            filterService={filterService}
            updateToolbar={updateToolbarBinded}
            resetToolbarVisibility={resetToolbarVisibility}
            legacyI18n={legacyI18n}
            i18n={i18n}
            permissions={permissions}
            {...filterProps}
          />
        ),
      };
    }));

    this.filterViewManager = (<FilterBarView />);
  }
  render() {
    const {
      serverContext: {
        CurrentAccount: { TimeZone },
      },
      panelView,
      showTopGridPanel,
      renderToolbarContainer,
      history,
      classes,
      selectedSubaccountId,
    } = this.props;

    const { hotelCenterI18n } = this.state;
    const topGridPanel = showTopGridPanel ? (<TopGridPanel panelView={panelView} />) : null;
    const topOfGridContent = (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <React.Fragment>
          {topGridPanel}
        </React.Fragment>
      </React.Suspense>
    );

    const stickyTopOfGridContent = (
      <React.Fragment>
        {renderToolbarContainer()}
        {this.filterViewManager}
      </React.Fragment>
    );
    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        {
          hotelCenterI18n ?
            <div className={showTopGridPanel ? 'hide' : ''}>
              <SubaccountSelectorPanel
                i18n={hotelCenterI18n}
                selectedSubaccountId={selectedSubaccountId}
                onSubaccountChanged={(subaccount) => { this.onSubaccountChangedFromSelector(subaccount); }}
                history={history}
                classes={classes}
              />
            </div> : null
        }
        <StyledTablePageContainer
          topOfGridContent={topOfGridContent}
          stickyTopOfGridContent={stickyTopOfGridContent}
          gridView={this.overviewGrid}
          backboneGridView={this.state.backboneGridView}
          customClasses={{
            tablePageContainer: `hotel-center-overview-page ${this.props.classes.overviewPage}`,
            gridViewContainer: 'hotel-center-overview-table',
          }}
          timeZone={TimeZone}
          scrollToTop={this.props.forceHideToolbar && this.props.showTopGridPanel}
          forceResetScrollFlag={this.props.forceResetScrollFlag}
        />
      </React.Suspense >
    );
  }
}

HotelCenterOverviewNew.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.object.isRequired,
    CurrentCustomer: PropTypes.object.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({}),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    route: PropTypes.string,
  })).isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    overviewPage: PropTypes.string,
  }).isRequired,
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  crossPageSelectPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  panelView: PropTypes.func.isRequired,
  onGridInjected: PropTypes.func.isRequired,
  showTopGridPanel: PropTypes.bool.isRequired,
  toolbarSelectedConfig: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  toolbarUnselectedConfig: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  updateToolbar: PropTypes.func.isRequired,
  resetToolbarVisibility: PropTypes.func.isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  forceHideToolbar: PropTypes.bool.isRequired,
  forceResetScrollFlag: PropTypes.bool.isRequired,
  gridName: PropTypes.string,
  selectedSubaccountId: PropTypes.string,
  updateSelectedSubaccount: PropTypes.func,
};

HotelCenterOverviewNew.defaultProps = {
  gridName: 'HotelSubAccountGrid',
  selectedSubaccountId: 'selectAllSubAccount',
  updateSelectedSubaccount: noop,
};

export const StyledOverviewPage = withDefaultStyles(
  withTablePageStateManager({ getToolbarConfig })(HotelCenterOverviewNew),
  getOverviewPageStyles
);
